/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import * as microsoftTeams from '@microsoft/teams-js';

// Component imports
import { Spin, message, notification } from 'antd';
import AnalyticsScreen from './AnalyticsScreen';
import AnalyticsPublicScreen from './AnalyticsPublicScreen';
import BotPreviewScreen from './BotPreviewScreen';
import BotScreen from './BotScreen';
import BotsScreen from './BotsScreen';
import DashboardScreen from './DashboardScreen';
import EmailVerificationScreen from './EmailVerificationScreen';
import EmptyLayout from '../components/Layout/EmptyLayout';
import LiveChatScreen from './LiveChatScreen';
import AuthorizeScreen from './AuthorizeScreen';
import LogoutScreen from './LogoutScreen';
import MainLayout from '../components/Layout/MainLayout';
import MediaScreen from './MediaScreen';
import ContactsScreen from './ContactsScreen';
import OrganizationSettingsScreen from './OrganizationSettingsScreen';
import OrganizationUsersScreen from './OrganizationUsersScreen';
import OrganizationLogsScreen from './OrganizationLogsScreen';
import PageNotFoundScreen from './PageNotFoundScreen';
import PricingScreen from './PricingScreen';
import PrivateRoute from './Routes/PrivateRoute';
import PublicRoute from './Routes/PublicRoute';
import TeamsLoginScreen from './TeamsLoginScreen';
import UserInvitationScreen from './UserInvitationScreen';
import ForgotPasswordScreen from './ForgotPasswordScreen';
import PasswordResetScreen from './PasswordResetScreen';
import RegistrationScreen from './RegistrationScreen';
import LoginScreen from './LoginScreen';
import AuthLayout from '../components/Layout/AuthLayout';
import HRWizConsoleScreen from './HRWizConsoleScreen';
import OrganizationKnowledgeBasesScreen from './OrganizationKnowledgeBasesScreen';
import OrganizationIntegrationsScreen from './OrganizationIntegrationsScreen';
import KnowledgeBasesForm from '../components/Forms/KnowledgeBases/KnowledgeBasesForm';
import ChatSessionsScreen from './ChatSessionsScreen';
import MissedQuestionsScreen from './MissedQuestionsScreen';
import SurveysScreen from './SurveysScreen';
import ScheduledReportsScreen from './ScheduledReportsScreen';
import MonitoringScreen from './MonitoringScreen';
import AutomatedAgentTestsScreen from './AutomatedAgentTestsScreen';
import SharePointConsentCallbackScreen from './SharePointConsentCallbackScreen';

// Misc imports
import { ADDITIONAL_ROLES_ORG, REFRESH_TOKEN_INTERVAL_MILLISECONDS, TEMP_STORAGE_KEYS } from '../constants/main';
import {
  NAV_ANALYTICS, NAV_ANALYTICS_PUBLIC, NAV_AUTHORIZE, NAV_BOT, NAV_BOTS, NAV_BOT_PREVIEW, NAV_CONTACTS, NAV_DASHBOARD,
  NAV_EMAIL_VERIFICATION, NAV_FORGOT_PASSWORD_REQUEST, NAV_FORGOT_PASSWORD, NAV_LIVECHAT, NAV_LIVECHAT_STANDALONE, NAV_LOGIN,
  NAV_LOGOUT, NAV_MEDIA, NAV_ORGANIZATION_INTEGRATIONS, NAV_ORGANIZATION_KNOWLEDGE_BASE, NAV_ORGANIZATION_KNOWLEDGE_BASES,
  NAV_ORGANIZATION_KNOWLEDGE_BASE_NEW, NAV_ORGANIZATION_LOGS, NAV_ORGANIZATION_SETTINGS, NAV_ORGANIZATION_USERS, NAV_ORG_EMPTY,
  NAV_PRICING, NAV_REGISTRATION, NAV_ROOT, NAV_USER_INVITATION, NAV_OIDC_CALLBACK, NAV_CHAT_SESSIONS, NAV_MISSED_QUESTIONS, NAV_SURVEYS,
  NAV_SCHEDULED_REPORTS, NAV_MONITORING, NAV_ORGANIZATION_TESTING, NAV_NO_BOT, NAV_BOT_LOADING,
  NAV_ORGANIZATION_RESULTS, NAV_SHAREPOINT_CONSENT_CALLBACK, NAV_INTERACTION_LOGS
} from '../constants/navigation';
import { IS_HRWIZ_CONSOLE_ENABLED, IS_CONTACTS_ENABLED, IS_LIVECHAT_CONVERSATIONS_ENABLED, IS_NOTIFICATIONS_ENABLED, IS_LOGIN_SCREEN_REDIRECT_DISABLED, IS_AUTOMATED_AGENT_TESTING_ENABLED, IS_LIVECHAT_CONSOLE_ONLY } from '../constants/env';
import { disconnectLivechatSocket, establishConnection } from '../store/liveChat/liveChatThunks';
import { isTokenAboutToExpire } from '../utils/auth/isTokenAboutToExpire';
import { updateRecentBots } from '../store/bots/botsActions';
import { useLazyFetchMeQuery, useRefreshTokenMutation } from '../store/auth/authApi';
import { disconnectPushNotifications, establishPushNotifications } from '../store/notifications/notificationsThunks';
import is from '../utils/is';
import replaceUrlParams from '../utils/string/replaceUrlParams';
import useUserPermissions from '../utils/hooks/useUserPermissions';
import hasLiveChatAccess from '../utils/misc/hasLiveChatAccess';
import AutomatedAgentTestsResultsScreen from './AutomatedAgentTestsResultsScreen';
import InteractionLogsScreen from './InteractionLogsScreen';

const ScreensRoot = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const inMicrosoftTeams = is.inMicrosoftTeams();

  const { currentUser, organizationId, token } = useSelector((state) => state.auth);
  const userPermissions = useUserPermissions(organizationId);
  const [fetchMe] = useLazyFetchMeQuery();
  const [refreshTokens] = useRefreshTokenMutation();

  const isTokenExpiring = useCallback(() => {
    return isTokenAboutToExpire(token);
  }, [token]);

  /** if the user won't be redirected away from the login screen, there is no need to fetch the user */
  const allowFetchingUser = useMemo(() => {
    return !(IS_LOGIN_SCREEN_REDIRECT_DISABLED && location.pathname === NAV_LOGIN);
  }, [location.pathname]);

  const isAuthenticated = !!currentUser;

  const handleTokenRefresh = async () => {
    if (token && allowFetchingUser) {
      if (isTokenExpiring()) {
        await refreshTokens({ refreshToken: window.localStorage.getItem(TEMP_STORAGE_KEYS.REFRESH_TOKEN) });
        await fetchMe();
      } else {
        await fetchMe();
      }
    }
  };

  useEffect(() => {
    if (inMicrosoftTeams) {
      /*
      * This has to be initialized as early as possible
      * and on the root component to prevent the
      * "There was a problem reaching this app" warning
      */
      const initializeTeamsSdk = async () => {
        await microsoftTeams.app.initialize();
      };

      initializeTeamsSdk();
    }

    if (IS_HRWIZ_CONSOLE_ENABLED) {
      return;
    }

    notification.config({
      maxCount: 2,
      duration: 0
    });

    message.config({
      maxCount: 3,
      duration: 7
    });
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      handleTokenRefresh();

      setInterval(() => {
        handleTokenRefresh();
      }, REFRESH_TOKEN_INTERVAL_MILLISECONDS);
    } else {
      dispatch(updateRecentBots([]));
    }
  }, [isAuthenticated, allowFetchingUser]);

  useEffect(() => {
    if (organizationId && isAuthenticated && IS_NOTIFICATIONS_ENABLED && !IS_LIVECHAT_CONSOLE_ONLY) {
      dispatch(establishPushNotifications(currentUser.id, organizationId));
    }

    if (IS_HRWIZ_CONSOLE_ENABLED) {
      return;
    }
    if (organizationId && isAuthenticated && userPermissions.includes(ADDITIONAL_ROLES_ORG.LIVE_AGENT)) {
      dispatch(establishConnection(navigate));
    }
    // Whenever the organization id or token changes value
    // we need to interrupt the current socket connection in
    // order to re-establish it with the correct credentials
    return () => {
      if (IS_HRWIZ_CONSOLE_ENABLED) {
        return;
      }
      dispatch(disconnectLivechatSocket());
      dispatch(disconnectPushNotifications());
    };
  }, [organizationId, isAuthenticated, token]);

  useEffect(() => {
    if (IS_HRWIZ_CONSOLE_ENABLED) {
      return;
    }
    notification.destroy();
  }, [organizationId]);

  // Routes shared between the HRwiz console and the main Chatbricks console
  const computeRoutes = () => {

    const commonAuthRoutes = [
      <Route
        path={NAV_LOGIN}
        key={NAV_LOGIN}
        exact
        element={
          <PublicRoute
            layout={AuthLayout}
            component={inMicrosoftTeams ? TeamsLoginScreen : LoginScreen}
          />
        }
      />,
      <Route
        path={NAV_LOGOUT}
        key={NAV_LOGOUT}
        exact
        element={<PrivateRoute layout={EmptyLayout} component={LogoutScreen} isAuthenticated={isAuthenticated} />}
      />,
      <Route
        path={NAV_REGISTRATION}
        key={NAV_REGISTRATION}
        exact
        element={<PublicRoute layout={AuthLayout} component={RegistrationScreen} />}
      />,
      <Route
        path={NAV_FORGOT_PASSWORD_REQUEST}
        key={NAV_FORGOT_PASSWORD_REQUEST}
        exact
        element={<PublicRoute layout={EmptyLayout} component={ForgotPasswordScreen} />}
      />,
      <Route
        path={NAV_FORGOT_PASSWORD}
        key={NAV_FORGOT_PASSWORD}
        exact
        element={<PublicRoute layout={EmptyLayout} component={PasswordResetScreen} />}
      />,
      <Route
        path={NAV_EMAIL_VERIFICATION}
        key={NAV_EMAIL_VERIFICATION}
        element={<PublicRoute layout={EmptyLayout} component={EmailVerificationScreen} />}
      />,
      <Route
        path={NAV_USER_INVITATION}
        key={NAV_USER_INVITATION}
        element={<PublicRoute layout={AuthLayout} component={UserInvitationScreen} />}
      />,
      <Route
        path={NAV_AUTHORIZE}
        key={NAV_AUTHORIZE}
        exact
        element={<PrivateRoute layout={EmptyLayout} component={AuthorizeScreen} isAuthenticated={isAuthenticated} />}
      />,
      <Route
        path={NAV_OIDC_CALLBACK}
        key={NAV_OIDC_CALLBACK}
        exact
        element={<PublicRoute layout={EmptyLayout} component={Spin} />}
      />,
      <Route
        path={NAV_SHAREPOINT_CONSENT_CALLBACK}
        key={NAV_SHAREPOINT_CONSENT_CALLBACK}
        exact
        element={<PublicRoute layout={EmptyLayout} component={SharePointConsentCallbackScreen} />}
      />
    ];

    if (IS_LIVECHAT_CONSOLE_ONLY) {
      let redirectPath;
      if (hasLiveChatAccess(currentUser, organizationId)) {
        // User is authenticated and has LiveChat access, send them to the live chat.
        redirectPath = replaceUrlParams(NAV_LIVECHAT, { orgId: organizationId });
      } else {
        redirectPath = NAV_LOGOUT;
      }

      return (
        <Routes>
          {commonAuthRoutes}
          <Route
            path={NAV_LIVECHAT}
            key={NAV_LIVECHAT}
            exact
            element={<PrivateRoute layout={MainLayout} component={LiveChatScreen} isAuthenticated={isAuthenticated} />}
          />

          <Route path={NAV_ROOT} key={NAV_ROOT} element={<Navigate to={redirectPath} replace />} />
          <Route path={NAV_ORG_EMPTY} key={NAV_ORG_EMPTY} element={<Navigate to={redirectPath} replace />} />
          <Route path="*" key="*" element={<Navigate to={redirectPath} replace />} />

        </Routes>
      );
    }

    const computedRoutes = [
      ...commonAuthRoutes,

      // 404 Routes, both for org-scoped and global paths
      <Route
        path="/org/:orgId/*"
        key="org-404"
        element={<PrivateRoute layout={MainLayout} component={PageNotFoundScreen} isAuthenticated={isAuthenticated} />}
      />,
      <Route
        path="*"
        key="*"
        element={<PublicRoute layout={isAuthenticated ? MainLayout : EmptyLayout} component={PageNotFoundScreen} />}
      />,

      <Route
        path={NAV_BOT_PREVIEW}
        key={NAV_BOT_PREVIEW}
        element={<PublicRoute layout={EmptyLayout} component={BotPreviewScreen} />}
      />,

      <Route
        path={NAV_ROOT}
        key={NAV_ROOT}
        exact
        element={<Navigate to={replaceUrlParams(NAV_DASHBOARD, { orgId: organizationId })} replace />}
      />,

      <Route
        path={NAV_ORG_EMPTY}
        key={NAV_ORG_EMPTY}
        exact
        element={<Navigate to={replaceUrlParams(NAV_DASHBOARD, { orgId: organizationId })} replace />}
      />,

      <Route
        path={NAV_DASHBOARD}
        key={NAV_DASHBOARD}
        exact
        element={<PrivateRoute layout={MainLayout} component={IS_HRWIZ_CONSOLE_ENABLED ? HRWizConsoleScreen : DashboardScreen} isAuthenticated={isAuthenticated} />}
      />,

      <Route
        path={NAV_NO_BOT}
        key={NAV_NO_BOT}
        exact
        element={<PrivateRoute layout={MainLayout} component={IS_HRWIZ_CONSOLE_ENABLED ? HRWizConsoleScreen : BotScreen} isAuthenticated={isAuthenticated} />}
      />,

      <Route
        path={NAV_BOT_LOADING}
        key={NAV_BOT_LOADING}
        exact
        element={<PrivateRoute layout={MainLayout} component={IS_HRWIZ_CONSOLE_ENABLED ? HRWizConsoleScreen : BotScreen} isAuthenticated={isAuthenticated} />}
      />,

      <Route
        path={NAV_BOT}
        key={NAV_BOT}
        exact
        element={<PrivateRoute layout={MainLayout} component={IS_HRWIZ_CONSOLE_ENABLED ? HRWizConsoleScreen : BotScreen} isAuthenticated={isAuthenticated} />}
      />,

      <Route
        path={NAV_ORGANIZATION_KNOWLEDGE_BASE}
        key={NAV_ORGANIZATION_KNOWLEDGE_BASE}
        exact
        element={<PrivateRoute layout={MainLayout} component={OrganizationKnowledgeBasesScreen} isAuthenticated={isAuthenticated} />}
      />
    ];

    // Chatbricks exclusive routes
    if (!IS_HRWIZ_CONSOLE_ENABLED) {
      const mainConsoleRoutes = [
        <Route
          path={NAV_MEDIA}
          key={NAV_MEDIA}
          exact
          element={<PrivateRoute layout={MainLayout} component={MediaScreen} isAuthenticated={isAuthenticated} />}
        />,

        <Route
          path={NAV_ANALYTICS_PUBLIC}
          key={NAV_ANALYTICS_PUBLIC}
          element={<PublicRoute layout={EmptyLayout} component={AnalyticsPublicScreen} />}
        />,

        <Route
          path={NAV_ANALYTICS}
          key={NAV_ANALYTICS}
          exact
          element={<PrivateRoute layout={MainLayout} component={AnalyticsScreen} isAuthenticated={isAuthenticated} />}
        />,

        <Route
          path={NAV_SCHEDULED_REPORTS}
          key={NAV_SCHEDULED_REPORTS}
          exact
          element={<PrivateRoute layout={MainLayout} component={ScheduledReportsScreen} isAuthenticated={isAuthenticated} />}
        />,

        <Route
          path={NAV_CHAT_SESSIONS}
          key={NAV_CHAT_SESSIONS}
          exact
          element={<PrivateRoute layout={MainLayout} component={ChatSessionsScreen} isAuthenticated={isAuthenticated} />}
        />,

        <Route
          path={NAV_MISSED_QUESTIONS}
          key={NAV_MISSED_QUESTIONS}
          exact
          element={<PrivateRoute layout={MainLayout} component={MissedQuestionsScreen} isAuthenticated={isAuthenticated} />}
        />,

        <Route
          path={NAV_SURVEYS}
          key={NAV_SURVEYS}
          exact
          element={<PrivateRoute layout={MainLayout} component={SurveysScreen} isAuthenticated={isAuthenticated} />}
        />,

        <Route
          path={NAV_MONITORING}
          key={NAV_MONITORING}
          exact
          element={<PrivateRoute layout={MainLayout} component={MonitoringScreen} isAuthenticated={isAuthenticated} />}
        />,

        <Route
          path={NAV_INTERACTION_LOGS}
          key={NAV_INTERACTION_LOGS}
          exact
          element={<PrivateRoute layout={MainLayout} component={InteractionLogsScreen} isAuthenticated={isAuthenticated} />}
        />,
        <Route
          path={NAV_NO_BOT}
          key={NAV_NO_BOT}
          exact
          element={<PrivateRoute layout={MainLayout} component={BotScreen} isAuthenticated={isAuthenticated} />}
        />,

        <Route
          path={NAV_BOTS}
          key={NAV_BOTS}
          exact
          element={<PrivateRoute layout={MainLayout} component={BotsScreen} isAuthenticated={isAuthenticated} />}
        />,

        <Route
          path={NAV_BOT_LOADING}
          key={NAV_BOT_LOADING}
          exact
          element={<PrivateRoute layout={MainLayout} component={BotScreen} isAuthenticated={isAuthenticated} />}
        />,
        <Route
          path={NAV_BOT}
          key={NAV_BOT}
          exact
          element={<PrivateRoute layout={MainLayout} component={BotScreen} isAuthenticated={isAuthenticated} />}
        />,

        <Route
          path={NAV_ORGANIZATION_SETTINGS}
          key={NAV_ORGANIZATION_SETTINGS}
          exact
          element={<PrivateRoute layout={MainLayout} component={OrganizationSettingsScreen} isAuthenticated={isAuthenticated} />}
        />,

        <Route
          path={NAV_ORGANIZATION_USERS}
          key={NAV_ORGANIZATION_USERS}
          exact
          element={<PrivateRoute layout={MainLayout} component={OrganizationUsersScreen} isAuthenticated={isAuthenticated} />}
        />,

        <Route
          path={NAV_ORGANIZATION_LOGS}
          key={NAV_ORGANIZATION_LOGS}
          exact
          element={<PrivateRoute layout={MainLayout} component={OrganizationLogsScreen} isAuthenticated={isAuthenticated} />}
        />,

        <Route
          path={NAV_PRICING}
          key={NAV_PRICING}
          exact
          element={<PublicRoute layout={isAuthenticated ? MainLayout : EmptyLayout} component={PricingScreen} />}
        />,

        <Route
          path={NAV_ORGANIZATION_INTEGRATIONS}
          key={NAV_ORGANIZATION_INTEGRATIONS}
          exact
          element={<PrivateRoute layout={MainLayout} component={OrganizationIntegrationsScreen} isAuthenticated={isAuthenticated} />}
        />,

        <Route
          path={NAV_ORGANIZATION_KNOWLEDGE_BASES}
          key={NAV_ORGANIZATION_KNOWLEDGE_BASES}
          exact
          element={<PrivateRoute layout={MainLayout} component={OrganizationKnowledgeBasesScreen} isAuthenticated={isAuthenticated} />}
        />,

        <Route
          path={NAV_ORGANIZATION_KNOWLEDGE_BASE_NEW}
          key={NAV_ORGANIZATION_KNOWLEDGE_BASE_NEW}
          exact
          element={<PrivateRoute layout={MainLayout} component={KnowledgeBasesForm} isAuthenticated={isAuthenticated} />}
        />,

        <Route
          path={NAV_ORGANIZATION_KNOWLEDGE_BASE}
          key={NAV_ORGANIZATION_KNOWLEDGE_BASE}
          exact
          element={<PrivateRoute layout={MainLayout} component={OrganizationKnowledgeBasesScreen} isAuthenticated={isAuthenticated} />}
        />
      ];

      // Live-Chat application routes
      if (IS_LIVECHAT_CONVERSATIONS_ENABLED) {
        mainConsoleRoutes.push.apply(mainConsoleRoutes, [
          <Route
            path={NAV_LIVECHAT}
            key={NAV_LIVECHAT}
            exact
            element={<PrivateRoute layout={MainLayout} component={LiveChatScreen} isAuthenticated={isAuthenticated} />}
          />,

          <Route
            path={NAV_LIVECHAT_STANDALONE}
            key={NAV_LIVECHAT_STANDALONE}
            exact
            element={<PrivateRoute layout={EmptyLayout} component={LiveChatScreen} isAuthenticated={isAuthenticated} />}
          />
        ]);
      }

      // Automated agent testing route
      if (IS_AUTOMATED_AGENT_TESTING_ENABLED) {
        mainConsoleRoutes.push(
          <Route
            path={NAV_ORGANIZATION_TESTING}
            key={NAV_ORGANIZATION_TESTING}
            exact
            element={<PrivateRoute layout={MainLayout} component={AutomatedAgentTestsScreen} isAuthenticated={isAuthenticated} />}
          />
        );

        mainConsoleRoutes.push(
          <Route
            path={NAV_ORGANIZATION_RESULTS}
            key={NAV_ORGANIZATION_RESULTS}
            exact
            element={<PrivateRoute layout={MainLayout} component={AutomatedAgentTestsResultsScreen} isAuthenticated={isAuthenticated} />}
          />
        );
      }

      // Contacts route
      if (IS_CONTACTS_ENABLED) {
        mainConsoleRoutes.push(
          <Route
            path={NAV_CONTACTS}
            key={NAV_CONTACTS}
            exact
            element={<PrivateRoute layout={MainLayout} component={ContactsScreen} isAuthenticated={isAuthenticated} />}
          />
        );
      }

      // Concat the shared routes with the Chatbricks routes
      computedRoutes.push.apply(computedRoutes, mainConsoleRoutes);
    }

    return (
      <Routes>
        {computedRoutes}
      </Routes>
    );
  };

  return computeRoutes();
};

export default ScreensRoot;
